import { FC, ReactNode, useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { Handle, Position } from '@xyflow/react'
import { keyframes } from '@mui/system'
import { WORKFLOW } from '../tokens'
import { WorkflowNodeType } from '../types'

export const nodeAppear = keyframes`
  from {
    opacity: 0;
    transform: scale(0.92) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`

interface HandleConfig {
  type: 'source' | 'target'
  position: Position
  id?: string
  style?: React.CSSProperties
}

interface BaseNodeProps {
  nodeType: WorkflowNodeType
  icon: ReactNode
  label: string
  nodeId?: string
  subtitle?: string
  selected?: boolean
  handles: HandleConfig[]
  children?: ReactNode
}

const CopyNodeId: FC<{ nodeId: string }> = ({ nodeId }) => {
  const [copied, setCopied] = useState(false)
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    void navigator.clipboard.writeText(nodeId)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(0,0,0,0.28)',
          fontSize: 9.5,
          fontFamily: '"SF Mono", "Fira Code", monospace',
          letterSpacing: '-0.02em',
          lineHeight: 1,
        }}
      >
        {nodeId}
      </Typography>
      <Tooltip title={copied ? 'Copied!' : 'Copy ID'} placement="right">
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{ p: 0, width: 14, height: 14 }}
        >
          {copied
            ? <CheckIcon sx={{ fontSize: 10, color: 'rgba(0,150,0,0.5)' }} />
            : <ContentCopyIcon sx={{ fontSize: 10, color: 'rgba(0,0,0,0.2)' }} />
          }
        </IconButton>
      </Tooltip>
    </Box>
  )
}

const BaseNode: FC<BaseNodeProps> = ({ nodeType, icon, label, nodeId, subtitle, selected, handles, children }) => {
  // eslint-disable-next-line security/detect-object-injection
  const colors = WORKFLOW[nodeType]

  return (
    <Box
      sx={{
        'width': WORKFLOW.node.width,
        'minHeight': WORKFLOW.node.minHeight,
        'borderRadius': WORKFLOW.node.borderRadius,
        'background': 'rgba(255,255,255,0.92)',
        'backdropFilter': 'blur(12px)',
        'border': selected
          ? `2px solid ${colors.accent}`
          : '1px solid rgba(0,0,0,0.06)',
        'boxShadow': selected
          ? `0 0 0 3px ${colors.border}, 0 8px 32px rgba(0,0,0,0.12)`
          : '0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)',
        'overflow': 'visible',
        'cursor': 'pointer',
        'transition': 'box-shadow 0.25s cubic-bezier(0.4,0,0.2,1), transform 0.25s cubic-bezier(0.4,0,0.2,1), border-color 0.2s ease',
        'animation': `${nodeAppear} 0.35s cubic-bezier(0.4,0,0.2,1) both`,
        'position': 'relative',
        '&:hover': {
          boxShadow: selected
            ? `0 0 0 3px ${colors.border}, 0 12px 40px rgba(0,0,0,0.15)`
            : '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
          transform: 'translateY(-2px)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          borderRadius: '14px 0 0 14px',
          background: `linear-gradient(180deg, ${colors.accent}, ${colors.accent}88)`,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '11px',
            background: `linear-gradient(135deg, ${colors.bg}, ${colors.border})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.accent,
            flexShrink: 0,
            boxShadow: `0 2px 8px ${colors.border}`,
            transition: 'transform 0.2s ease',
          }}
        >
          {icon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: 13,
              color: 'rgba(0,0,0,0.85)',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              letterSpacing: '-0.01em',
            }}
          >
            {label}
          </Typography>
          {nodeId && <CopyNodeId nodeId={nodeId} />}
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(0,0,0,0.4)',
                lineHeight: 1.3,
                fontSize: 11,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                fontFamily: '"SF Mono", "Fira Code", "Cascadia Code", monospace',
                letterSpacing: '-0.02em',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {children && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          {children}
        </Box>
      )}

      {handles.map((handle, index) => (
        <Handle
          key={`${handle.type}-${handle.position}-${handle.id ?? index}`}
          type={handle.type}
          position={handle.position}
          id={handle.id}
          style={{
            width: 12,
            height: 12,
            background: '#fff',
            border: `2.5px solid ${colors.accent}`,
            boxShadow: '0 0 0 2px rgba(255,255,255,0.9), 0 2px 6px rgba(0,0,0,0.12)',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            ...handle.style,
          }}
        />
      ))}
    </Box>
  )
}

export default BaseNode
